"use client";

import { c as _c } from "react/compiler-runtime";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { getDefaultQuickVoteAmount, normalizeQuickVoteAmount } from "@/hooks/memesQuickVote.helpers";
import { useMemesQuickVoteQueue } from "@/hooks/useMemesQuickVoteQueue";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import MemesQuickVoteControls from "./MemesQuickVoteControls";
import MemesQuickVoteDialogSkeleton from "./MemesQuickVoteDialogSkeleton";
import MemesQuickVotePreview from "./MemesQuickVotePreview";
interface MemesQuickVoteDialogProps {
  readonly isOpen: boolean;
  readonly sessionId: number;
  readonly onClose: () => void;
}
interface MemesQuickVoteDialogContentProps {
  readonly activeDrop: NonNullable<ReturnType<typeof useMemesQuickVoteQueue>["activeDrop"]>;
  readonly isMobile: boolean;
  readonly latestUsedAmount: number | null;
  readonly remainingCount: number;
  readonly recentAmounts: number[];
  readonly submitVote: ReturnType<typeof useMemesQuickVoteQueue>["submitVote"];
  readonly skipDrop: ReturnType<typeof useMemesQuickVoteQueue>["skipDrop"];
  readonly uncastPower: number | null;
  readonly votingLabel: string | null;
}
function MemesQuickVoteDialogContent(t0) {
  const $ = _c(65);
  const {
    activeDrop,
    isMobile,
    latestUsedAmount,
    remainingCount,
    recentAmounts,
    submitVote,
    skipDrop,
    uncastPower,
    votingLabel
  } = t0;
  const maxRating = activeDrop.context_profile_context?.max_rating ?? 0;
  let t1;
  if ($[0] !== maxRating) {
    t1 = maxRating > 0 ? getDefaultQuickVoteAmount(maxRating) : 1;
    $[0] = maxRating;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const defaultAmount = t1;
  let t2;
  if ($[2] !== defaultAmount) {
    t2 = () => `${defaultAmount}`;
    $[2] = defaultAmount;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [customValue, setCustomValue] = useState(t2);
  let t3;
  if ($[4] !== recentAmounts.length) {
    t3 = () => recentAmounts.length === 0;
    $[4] = recentAmounts.length;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [isCustomOpen, setIsCustomOpen] = useState(t3);
  const [isAdvancing, setIsAdvancing] = useState(false);
  let t4;
  if ($[6] !== latestUsedAmount || $[7] !== maxRating) {
    t4 = latestUsedAmount === null ? null : normalizeQuickVoteAmount(latestUsedAmount, maxRating);
    $[6] = latestUsedAmount;
    $[7] = maxRating;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const normalizedLatestUsedAmount = t4;
  let t5;
  if ($[9] !== customValue || $[10] !== maxRating) {
    t5 = normalizeQuickVoteAmount(customValue, maxRating);
    $[9] = customValue;
    $[10] = maxRating;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const normalizedCustomAmount = t5;
  let t6;
  bb0: {
    if (maxRating <= 0) {
      let t7;
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = [];
        $[12] = t7;
      } else {
        t7 = $[12];
      }
      t6 = t7;
      break bb0;
    }
    let t7;
    if ($[13] !== maxRating || $[14] !== recentAmounts) {
      let t8;
      if ($[16] !== maxRating) {
        t8 = amount => normalizeQuickVoteAmount(amount, maxRating);
        $[16] = maxRating;
        $[17] = t8;
      } else {
        t8 = $[17];
      }
      t7 = Array.from(new Set(recentAmounts.map(t8).filter(_temp))).sort(_temp2);
      $[13] = maxRating;
      $[14] = recentAmounts;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    t6 = t7;
  }
  const visibleQuickAmounts = t6;
  const swipeVoteAmount = isCustomOpen ? normalizedCustomAmount : normalizedLatestUsedAmount ?? normalizedCustomAmount;
  let t7;
  if ($[18] !== activeDrop || $[19] !== submitVote) {
    t7 = async amount_1 => {
      const wasQueued = await submitVote(activeDrop, amount_1);
      if (!wasQueued) {
        setIsAdvancing(false);
      }
    };
    $[18] = activeDrop;
    $[19] = submitVote;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const queueVoteAmount = t7;
  let t8;
  if ($[21] !== isAdvancing || $[22] !== queueVoteAmount) {
    t8 = async amount_2 => {
      if (isAdvancing) {
        return;
      }
      setIsAdvancing(true);
      await queueVoteAmount(amount_2);
    };
    $[21] = isAdvancing;
    $[22] = queueVoteAmount;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const handleVoteAmount = t8;
  let t9;
  if ($[24] !== activeDrop || $[25] !== skipDrop) {
    t9 = () => {
      skipDrop(activeDrop);
    };
    $[24] = activeDrop;
    $[25] = skipDrop;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const queueSkip = t9;
  let t10;
  if ($[27] !== isAdvancing || $[28] !== queueSkip) {
    t10 = () => {
      if (isAdvancing) {
        return;
      }
      setIsAdvancing(true);
      queueSkip();
    };
    $[27] = isAdvancing;
    $[28] = queueSkip;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const handleSkip = t10;
  let t11;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setIsAdvancing(true);
    };
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== queueVoteAmount || $[32] !== swipeVoteAmount) {
    t12 = () => {
      if (swipeVoteAmount === null) {
        setIsAdvancing(false);
        return;
      }
      queueVoteAmount(swipeVoteAmount);
    };
    $[31] = queueVoteAmount;
    $[32] = swipeVoteAmount;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== activeDrop || $[35] !== isAdvancing || $[36] !== isMobile || $[37] !== queueSkip || $[38] !== remainingCount || $[39] !== swipeVoteAmount || $[40] !== t12 || $[41] !== uncastPower || $[42] !== votingLabel) {
    t13 = <MemesQuickVotePreview drop={activeDrop} isBusy={isAdvancing} isMobile={isMobile} remainingCount={remainingCount} swipeVoteAmount={swipeVoteAmount} uncastPower={uncastPower} votingLabel={votingLabel} onAdvanceStart={t11} onSkip={queueSkip} onVoteWithSwipe={t12} />;
    $[34] = activeDrop;
    $[35] = isAdvancing;
    $[36] = isMobile;
    $[37] = queueSkip;
    $[38] = remainingCount;
    $[39] = swipeVoteAmount;
    $[40] = t12;
    $[41] = uncastPower;
    $[42] = votingLabel;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = value => {
      if (value === "") {
        setCustomValue("");
        return;
      }
      setCustomValue(value.replace(/[^\d]/g, ""));
    };
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== customValue || $[46] !== handleVoteAmount) {
    t15 = async () => {
      await handleVoteAmount(customValue);
    };
    $[45] = customValue;
    $[46] = handleVoteAmount;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      setIsCustomOpen(_temp3);
    };
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== activeDrop || $[50] !== customValue || $[51] !== handleSkip || $[52] !== handleVoteAmount || $[53] !== isAdvancing || $[54] !== isCustomOpen || $[55] !== normalizedLatestUsedAmount || $[56] !== remainingCount || $[57] !== t15 || $[58] !== uncastPower || $[59] !== visibleQuickAmounts || $[60] !== votingLabel) {
    t17 = <div className="md:tw-sticky md:tw-top-0 md:tw-self-stretch"><MemesQuickVoteControls customValue={customValue} drop={activeDrop} isCustomOpen={isCustomOpen} isSubmitting={isAdvancing} latestUsedAmount={normalizedLatestUsedAmount} remainingCount={remainingCount} quickAmounts={visibleQuickAmounts} uncastPower={uncastPower} votingLabel={votingLabel} onCustomChange={t14} onCustomSubmit={t15} onOpenCustom={t16} onSkip={handleSkip} onVoteAmount={handleVoteAmount} /></div>;
    $[49] = activeDrop;
    $[50] = customValue;
    $[51] = handleSkip;
    $[52] = handleVoteAmount;
    $[53] = isAdvancing;
    $[54] = isCustomOpen;
    $[55] = normalizedLatestUsedAmount;
    $[56] = remainingCount;
    $[57] = t15;
    $[58] = uncastPower;
    $[59] = visibleQuickAmounts;
    $[60] = votingLabel;
    $[61] = t17;
  } else {
    t17 = $[61];
  }
  let t18;
  if ($[62] !== t13 || $[63] !== t17) {
    t18 = <div className="tw-grid tw-gap-6 md:tw-grid-cols-[minmax(0,1.35fr)_minmax(22rem,0.95fr)] md:tw-items-stretch">{t13}{t17}</div>;
    $[62] = t13;
    $[63] = t17;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  return t18;
}
function _temp3(current) {
  return !current;
}
function _temp2(left, right) {
  return left - right;
}
function _temp(amount_0) {
  return amount_0 !== null;
}
function MemesQuickVoteDialogDoneState() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-flex tw-h-full tw-min-h-80 tw-items-center tw-justify-center"><div className="tw-max-w-md tw-text-center"><p className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-white">You are done</p><p className="tw-mb-0 tw-text-sm tw-text-iron-400">No unrated memes are left in quick vote right now.</p></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function MemesQuickVoteDialogErrorState(t0) {
  const $ = _c(4);
  const {
    onRetry
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="tw-mb-2 tw-text-lg tw-font-semibold tw-text-white">Couldn't load your queue</p>;
    t2 = <p className="tw-mb-4 tw-text-sm tw-text-iron-400">Quick vote couldn't reach the leaderboard. Try again.</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onRetry) {
    t3 = <div className="tw-flex tw-h-full tw-min-h-80 tw-items-center tw-justify-center"><div className="tw-max-w-md tw-text-center">{t1}{t2}<button type="button" onClick={onRetry} className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-white/10 tw-bg-white/5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-transition-colors desktop-hover:hover:tw-border-white/20 desktop-hover:hover:tw-bg-white/10">Try again</button></div></div>;
    $[2] = onRetry;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
export default function MemesQuickVoteDialog(t0) {
  const $ = _c(41);
  const {
    isOpen,
    sessionId,
    onClose
  } = t0;
  const dialogRef = useRef(null);
  const previouslyFocusedElementRef = useRef(null);
  const previousBodyOverflowRef = useRef("");
  const isMobile = useIsMobileScreen();
  let t1;
  if ($[0] !== isOpen || $[1] !== sessionId) {
    t1 = {
      enabled: isOpen,
      sessionId
    };
    $[0] = isOpen;
    $[1] = sessionId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    activeDrop,
    hasDiscoveryError,
    isExhausted,
    isLoading,
    latestUsedAmount,
    recentAmounts,
    remainingCount,
    retryDiscovery,
    submitVote,
    skipDrop,
    uncastPower,
    votingLabel
  } = useMemesQuickVoteQueue(t1);
  let t2;
  let t3;
  if ($[3] !== isOpen) {
    t2 = () => {
      const dialog = dialogRef.current;
      if (!dialog) {
        return;
      }
      if (!isOpen) {
        if (dialog.open) {
          dialog.close();
        }
        document.body.style.overflow = previousBodyOverflowRef.current;
        previouslyFocusedElementRef.current?.focus();
        return;
      }
      previouslyFocusedElementRef.current = document.activeElement as HTMLElement;
      previousBodyOverflowRef.current = document.body.style.overflow;
      if (!dialog.open) {
        dialog.showModal();
      }
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => {
        const autofocusTarget = dialog.querySelector("[data-autofocus='true']");
        autofocusTarget?.focus();
      });
      return () => {
        document.body.style.overflow = previousBodyOverflowRef.current;
      };
    };
    t3 = [isOpen];
    $[3] = isOpen;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] !== onClose) {
    t4 = () => {
      const dialog_0 = dialogRef.current;
      if (!dialog_0) {
        return;
      }
      const handleCancel = event => {
        event.preventDefault();
        onClose();
      };
      dialog_0.addEventListener("cancel", handleCancel);
      return () => {
        dialog_0.removeEventListener("cancel", handleCancel);
      };
    };
    t5 = [onClose];
    $[6] = onClose;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let dialogBody;
  if (isExhausted) {
    let t6;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <MemesQuickVoteDialogDoneState />;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    dialogBody = t6;
  } else {
    if (!activeDrop && hasDiscoveryError) {
      let t6;
      if ($[10] !== retryDiscovery) {
        t6 = <MemesQuickVoteDialogErrorState onRetry={retryDiscovery} />;
        $[10] = retryDiscovery;
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      dialogBody = t6;
    } else {
      if (!activeDrop && isLoading) {
        let t6;
        if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
          t6 = <MemesQuickVoteDialogSkeleton />;
          $[12] = t6;
        } else {
          t6 = $[12];
        }
        dialogBody = t6;
      } else {
        if (!activeDrop) {
          let t6;
          if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
            t6 = <MemesQuickVoteDialogSkeleton />;
            $[13] = t6;
          } else {
            t6 = $[13];
          }
          dialogBody = t6;
        } else {
          const t6 = `${sessionId}:${activeDrop.serial_no}`;
          let t7;
          if ($[14] !== activeDrop || $[15] !== isMobile || $[16] !== latestUsedAmount || $[17] !== recentAmounts || $[18] !== remainingCount || $[19] !== skipDrop || $[20] !== submitVote || $[21] !== t6 || $[22] !== uncastPower || $[23] !== votingLabel) {
            t7 = <MemesQuickVoteDialogContent key={t6} activeDrop={activeDrop} isMobile={isMobile} latestUsedAmount={latestUsedAmount} remainingCount={remainingCount} recentAmounts={recentAmounts} submitVote={submitVote} skipDrop={skipDrop} uncastPower={uncastPower} votingLabel={votingLabel} />;
            $[14] = activeDrop;
            $[15] = isMobile;
            $[16] = latestUsedAmount;
            $[17] = recentAmounts;
            $[18] = remainingCount;
            $[19] = skipDrop;
            $[20] = submitVote;
            $[21] = t6;
            $[22] = uncastPower;
            $[23] = votingLabel;
            $[24] = t7;
          } else {
            t7 = $[24];
          }
          dialogBody = t7;
        }
      }
    }
  }
  let t6;
  if ($[25] !== onClose) {
    t6 = event_0 => {
      if (event_0.target !== event_0.currentTarget) {
        return;
      }
      onClose();
    };
    $[25] = onClose;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  let t7;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <XMarkIcon className="tw-size-5" />;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== onClose) {
    t8 = <button type="button" data-autofocus="true" onClick={onClose} className="tw-absolute tw-right-3 tw-top-[calc(env(safe-area-inset-top,0px)+0.75rem)] tw-z-10 tw-inline-flex tw-size-11 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-white/10 tw-bg-iron-900/90 tw-text-iron-300 tw-shadow-[0_18px_40px_rgba(0,0,0,0.35)] tw-backdrop-blur-sm tw-transition-colors disabled:tw-cursor-not-allowed disabled:tw-opacity-60 desktop-hover:hover:tw-border-white/20 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-white md:tw-right-4 md:tw-top-4" aria-label="Close quick vote">{t7}</button>;
    $[28] = onClose;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== dialogBody) {
    t9 = <div className="tw-flex-1 tw-overflow-y-auto tw-p-4 tw-pt-16 md:tw-p-6">{dialogBody}</div>;
    $[30] = dialogBody;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== t8 || $[33] !== t9) {
    t10 = <div className="tw-relative tw-flex tw-h-full tw-w-full tw-flex-col tw-bg-iron-950 md:tw-h-auto md:tw-max-h-[min(92vh,56rem)] md:tw-max-w-5xl md:tw-overflow-hidden md:tw-rounded-[2rem] md:tw-border md:tw-border-solid md:tw-border-white/10 md:tw-shadow-[0_30px_80px_rgba(0,0,0,0.45)]">{t8}{t9}</div>;
    $[32] = t8;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== t6) {
    t11 = <div className="tw-flex tw-h-full tw-w-full tw-items-stretch tw-justify-center tw-bg-iron-950/85 md:tw-items-center md:tw-p-6" onClick={t6}>{t10}</div>;
    $[35] = t10;
    $[36] = t6;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== sessionId || $[39] !== t11) {
    t12 = <dialog ref={dialogRef} data-session-id={sessionId} className="tailwind-scope tw-m-0 tw-h-screen tw-w-screen tw-max-w-none tw-border-none tw-bg-transparent tw-p-0" aria-label="Memes quick vote">{t11}</dialog>;
    $[38] = sessionId;
    $[39] = t11;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  return t12;
}